import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import './SetShiftOutcomesPanel.css';

const STATUS_LABELS = {
  HONOURED: 'Honoured',
  DROPPED_AVAILABILITY: 'Dropped (availability)',
  DROPPED_EVENT_OVERLAP: 'Dropped (event overlap)',
  DROPPED_SOFT: 'Dropped (soft)',
};

const STATUS_CLASSES = {
  HONOURED: 'set-shift-pill--honoured',
  DROPPED_AVAILABILITY: 'set-shift-pill--availability',
  DROPPED_EVENT_OVERLAP: 'set-shift-pill--event',
  DROPPED_SOFT: 'set-shift-pill--soft',
};

const resolveEmployeeName = (employeeId, employees) => {
  if (!employees) return employeeId;
  if (Array.isArray(employees)) {
    const match = employees.find(m => m && m.Id === employeeId);
    if (match && match.Name) return match.Name;
  } else if (typeof employees === 'object') {
    const direct = employees[employeeId];
    if (direct && direct.Name) return direct.Name;
    if (typeof direct === 'string') return direct;
  }
  return employeeId;
};

const formatRange = (outcome) => {
  const start = outcome.start || '';
  const end = outcome.end || '';
  if (!start && !end) return '';
  return `${start}–${end}`;
};

const SetShiftOutcomesPanel = ({ outcomes, employees }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const safeOutcomes = useMemo(
    () => (Array.isArray(outcomes) ? outcomes : []),
    [outcomes],
  );

  const counts = useMemo(() => {
    const base = { HONOURED: 0, DROPPED_AVAILABILITY: 0, DROPPED_EVENT_OVERLAP: 0, DROPPED_SOFT: 0 };
    safeOutcomes.forEach(o => {
      if (o && o.status && base[o.status] !== undefined) base[o.status] += 1;
    });
    return base;
  }, [safeOutcomes]);

  const dropped = useMemo(
    () => safeOutcomes.filter(o => o && o.status && o.status !== 'HONOURED'),
    [safeOutcomes],
  );

  if (safeOutcomes.length === 0) {
    return (
      <div className="set-shift-outcomes-panel set-shift-outcomes-panel--empty">
        <span className="set-shift-outcomes-empty">No set shifts in this request.</span>
      </div>
    );
  }

  if (dropped.length === 0) {
    return (
      <div className="set-shift-outcomes-panel set-shift-outcomes-panel--all-honoured">
        <span className="set-shift-outcomes-empty">
          All {counts.HONOURED} set shift{counts.HONOURED === 1 ? '' : 's'} honoured.
        </span>
      </div>
    );
  }

  const countLine = [
    `${counts.HONOURED} honoured`,
    counts.DROPPED_AVAILABILITY > 0 && `${counts.DROPPED_AVAILABILITY} dropped (availability)`,
    counts.DROPPED_EVENT_OVERLAP > 0 && `${counts.DROPPED_EVENT_OVERLAP} dropped (event overlap)`,
    counts.DROPPED_SOFT > 0 && `${counts.DROPPED_SOFT} dropped (soft)`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="set-shift-outcomes-panel">
      <div
        className="set-shift-outcomes-header"
        onClick={() => setIsExpanded(prev => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') setIsExpanded(prev => !prev);
        }}
      >
        <h3 className="set-shift-outcomes-title">Set shift outcomes</h3>
        <span className="set-shift-outcomes-counts">{countLine}</span>
        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </div>
      {isExpanded && (
        <ul className="set-shift-outcomes-list">
          {dropped.map((o, idx) => {
            const name = resolveEmployeeName(o.employee_id, employees);
            const status = o.status || 'HONOURED';
            const label = STATUS_LABELS[status] || status;
            const pillClass = STATUS_CLASSES[status] || '';
            return (
              <li key={`${o.employee_id || 'unknown'}-${o.date || ''}-${o.start || ''}-${idx}`} className="set-shift-outcomes-item">
                <div className="set-shift-outcomes-item-head">
                  <span className="set-shift-outcomes-employee">{name}</span>
                  <span className="set-shift-outcomes-time">
                    {o.date ? `${o.date} ` : ''}{formatRange(o)}
                  </span>
                  <span className={`set-shift-pill ${pillClass}`}>{label}</span>
                </div>
                {o.reason && (
                  <div className="set-shift-outcomes-reason">{o.reason}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SetShiftOutcomesPanel;
